/**
 * Field validation utilities for workflow forms
 * Provides validation functions and error message generation for form fields
 */

import {
  DATE_STRING_FORMAT,
  EMAIL_FORMAT,
  NON_DIGIT_CHARS,
  PASSPORT_NUMBER_FORMAT,
  SSN_FORMAT,
  SSN_FORMAT_NON_PROD,
  VISA_NUMBER_FORMAT,
  WORK_PERMIT_NUMBER_FORMAT,
  ZIP_CODE_FORMAT,
} from '@access-cms/shared-constants';

import { FieldErrors } from '../types';

import { normalizeUsPhoneNumber } from '@/utils/phoneNumber';

const RELAXED_SSN_ENVIRONMENTS = ['dev', 'development', 'test'];
const ON_FILE_SSN_PATTERN = /^\*{8}\d$/;

function isOnFileSsn(value: string): boolean {
  return ON_FILE_SSN_PATTERN.test(value);
}

/**
 * Login.gov / IDM test accounts use SSNs in the 666 and 900-999 area ranges,
 * which the SSA rules reject. Dev and test relax the range check so those
 * accounts can complete the application. Impl, prod, missing, or unrecognized
 * values stay strict, so production and impl can never relax.
 */
export function getSsnFormat(environment: string | undefined = import.meta.env.VITE_ENVIRONMENT): RegExp {
  const normalized = environment?.trim().toLowerCase();

  return normalized && RELAXED_SSN_ENVIRONMENTS.includes(normalized) ? SSN_FORMAT_NON_PROD : SSN_FORMAT;
}

/**
 * `allData` is the sub-step being validated. `allFormData` is the whole application,
 * for the few rules whose requirement depends on an answer given in another sub-step
 * (which documents an upload needs depends on citizenship).
 */
export interface FieldValidationRule {
  validate: (value: unknown, allData?: Record<string, unknown>, allFormData?: Record<string, unknown>) => boolean;
  errorMessage: string;
}

export interface FieldValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates a field against an array of validation rules
 */
export function validateField(
  value: unknown,
  rules: FieldValidationRule[],
  allData?: Record<string, unknown>,
  allFormData?: Record<string, unknown>,
): FieldValidationResult {
  for (const rule of rules) {
    if (!rule.validate(value, allData, allFormData)) {
      return {
        errorMessage: rule.errorMessage,
        isValid: false,
      };
    }
  }

  return { isValid: true };
}

/**
 * Common validation rules
 */

export const required: FieldValidationRule = {
  errorMessage: 'This field is required',
  validate: (value: unknown) => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return value !== null && value !== undefined;
  },
};

/**
 * ACP-2077 (Section 508): required, for a Yes/No question. The generic "This field is
 * required" names nothing; the question beside it does, so the message says what to do.
 */
export const requiredChoice: FieldValidationRule = {
  errorMessage: 'Select Yes or No',
  validate: required.validate,
};

/**
 * ACP-2077 (Section 508): required, with a message that names the field — for fields
 * whose label is a question, which "<label> is required" would read badly.
 */
export function requiredAs(errorMessage: string): FieldValidationRule {
  return { errorMessage, validate: required.validate };
}

export const email: FieldValidationRule = {
  errorMessage: 'Please enter a valid email address',
  validate: (value: unknown) => {
    if (typeof value !== 'string' || value.trim().length === 0) {
      return true; // Let required rule handle empty values
    }

    return EMAIL_FORMAT.test(value);
  },
};

export function minLength(length: number): FieldValidationRule {
  return {
    errorMessage: `Must be at least ${length} characters`,
    validate: (value: unknown) => {
      if (typeof value !== 'string') return false;
      if (value.trim().length === 0) return true; // Let required rule handle empty values

      return value.trim().length >= length;
    },
  };
}

export function maxLength(length: number): FieldValidationRule {
  return {
    errorMessage: `Must be no more than ${length} characters`,
    validate: (value: unknown) => {
      if (typeof value !== 'string') return false;

      return value.length <= length;
    },
  };
}

export function exactLength(length: number, fieldName = 'field'): FieldValidationRule {
  return {
    errorMessage: `${fieldName} must be exactly ${length} digits`,
    validate: (value: unknown) => {
      if (typeof value !== 'string') return false;
      if (value.trim().length === 0) return true; // Let required rule handle empty values
      const digitsOnly = value.replace(NON_DIGIT_CHARS, '');

      return digitsOnly.length === length;
    },
  };
}

export const zipCode: FieldValidationRule = {
  errorMessage: 'ZIP code must be 5 digits (12345) or 9 digits with dash (12345-6789)',
  validate: (value: unknown) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true; // Let required rule handle empty values

    return ZIP_CODE_FORMAT.test(value.trim());
  },
};

export const phoneNumber: FieldValidationRule = {
  errorMessage: 'Phone number must be exactly 10 digits',
  validate: (value: unknown) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true; // Let required rule handle empty values
    const digitsOnly = normalizeUsPhoneNumber(value);

    return digitsOnly.length === 10;
  },
};

export const ssn: FieldValidationRule = {
  errorMessage:
    getSsnFormat() === SSN_FORMAT
      ? 'Please enter a valid Social Security Number (XXX-XX-XXXX). Cannot start with 000, 666, or 900-999.'
      : 'Please enter a valid Social Security Number (XXX-XX-XXXX). Cannot start with 000.',
  validate: (value: unknown) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true;
    if (isOnFileSsn(value)) return true;

    if (/[a-zA-Z]/.test(value)) return false;

    const digitsOnly = value.replace(NON_DIGIT_CHARS, '');

    if (digitsOnly.length < 9) return true;
    if (digitsOnly.length > 9) return false;

    const formatted = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 5)}-${digitsOnly.slice(5, 9)}`;

    return getSsnFormat().test(formatted);
  },
};

export const ssnConfirmMatch: FieldValidationRule = {
  errorMessage: 'Social Security Numbers must match.',
  validate: (value: unknown, allData?: Record<string, unknown>) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true;
    const ssnValue = allData?.ssn;
    if (typeof ssnValue === 'string' && isOnFileSsn(value) && value === ssnValue) {
      return true;
    }
    const confirmDigits = value.replace(NON_DIGIT_CHARS, '');

    if (typeof ssnValue !== 'string') return false;
    const ssnDigits = ssnValue.replace(NON_DIGIT_CHARS, '');

    if (confirmDigits.length < 9) return true;

    return confirmDigits === ssnDigits;
  },
};

export function pattern(regex: RegExp, message: string): FieldValidationRule {
  return {
    errorMessage: message,
    validate: (value: unknown) => {
      if (typeof value !== 'string') return false;
      if (value.trim().length === 0) return true; // Let required rule handle empty values

      return regex.test(value);
    },
  };
}

export const visaNumber: FieldValidationRule = {
  errorMessage:
    'Visa number must be 8 characters: either all digits (12345678) or 1 letter followed by 7 digits (A1234567)',
  validate: (value: unknown) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true; // Let required rule handle empty values

    return VISA_NUMBER_FORMAT.test(value.trim().toUpperCase());
  },
};

export const passportNumber: FieldValidationRule = {
  errorMessage: 'Passport number must be 6-15 alphanumeric characters (letters and numbers only)',
  validate: (value: unknown) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true; // Let required rule handle empty values

    return PASSPORT_NUMBER_FORMAT.test(value.trim());
  },
};

export const workPermitNumber: FieldValidationRule = {
  errorMessage: 'Work Permit Number must be 13 characters: 3 letters followed by 10 digits (e.g., EAC1234567890)',
  validate: (value: unknown) => {
    if (typeof value !== 'string') return false;
    if (value.trim().length === 0) return true; // Let required rule handle empty values

    return WORK_PERMIT_NUMBER_FORMAT.test(value.trim().toUpperCase());
  },
};

export function custom(
  validationFn: (value: unknown, allData?: Record<string, unknown>, allFormData?: Record<string, unknown>) => boolean,
  message: string,
): FieldValidationRule {
  return {
    errorMessage: message,
    validate: validationFn,
  };
}

/**
 * Field validation configuration type
 */
export interface FieldValidationConfig {
  [fieldName: string]: FieldValidationRule[];
}

/**
 * Gets a nested value from an object using dot notation path
 * e.g., getNestedValue(obj, 'a.b.c') returns obj.a.b.c
 *
 * Exported because field-error keys follow the same dot notation, so anything
 * comparing a field's old and new value has to resolve the path the same way.
 */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.');
  let value: unknown = obj;

  for (const key of keys) {
    if (value === null || value === undefined || typeof value !== 'object') {
      return undefined;
    }
    value = (value as Record<string, unknown>)[key];
  }

  return value;
}

/**
 * Validates multiple fields against their validation configurations
 * Supports dot notation for nested field paths (e.g., 'draftItem.fieldName')
 */
export function validateFields(
  data: Record<string, unknown>,
  config: FieldValidationConfig,
  allFormData?: Record<string, unknown>,
): FieldErrors {
  const errors: FieldErrors = {};

  Object.keys(config).forEach((fieldName) => {
    const rules = config[fieldName];
    // Support dot notation for nested field access
    const value = fieldName.includes('.') ? getNestedValue(data, fieldName) : data[fieldName];
    const result = validateField(value, rules, data, allFormData);

    if (!result.isValid) {
      errors[fieldName] = result.errorMessage;
    }
  });

  return errors;
}

/**
 * Checks if there are any errors in the errors object
 */
export function hasErrors(errors: FieldErrors): boolean {
  return Object.values(errors).some((error) => error !== undefined && error !== '');
}

/**
 * Reusable validation helper functions for workflow validate callbacks
 * These ensure consistency across all validation logic
 */

/**
 * Validates that a string field exists and is not empty after trimming
 * Accepts unknown type and performs type checking internally
 */
export function isNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Validates email format
 */
export function isValidEmail(email: string | undefined | null): boolean {
  if (!email) return false;

  return EMAIL_FORMAT.test(email.trim());
}

/**
 * Validates phone number has exactly 10 digits (ignoring formatting)
 */
export function isValidPhoneNumber(phone: string | undefined | null): boolean {
  if (!phone) return false;
  const digitsOnly = normalizeUsPhoneNumber(phone);

  return digitsOnly.length === 10;
}

/**
 * Validates SSN has exactly 9 digits and follows SSN rules (ignoring formatting)
 * - Area number cannot be 000, 666, or 900-999
 * - Group number cannot be 00
 * - Serial number cannot be 0000
 */
export function isValidSSN(ssn: string | undefined | null, environment?: string): boolean {
  if (!ssn) return false;
  if (isOnFileSsn(ssn)) return true;
  const digitsOnly = ssn.replace(NON_DIGIT_CHARS, '');

  if (digitsOnly.length !== 9) return false;

  const formatted = `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 5)}-${digitsOnly.slice(5, 9)}`;

  return getSsnFormat(environment ?? import.meta.env.VITE_ENVIRONMENT).test(formatted);
}

/**
 * Validates zip code has either 5 or 9 digits (ignoring formatting)
 * Accepts: 12345 or 12345-6789
 */
export function isValidZipCode(zipCode: string | undefined | null): boolean {
  if (!zipCode) return false;
  const digitsOnly = zipCode.replace(NON_DIGIT_CHARS, '');

  return digitsOnly.length === 5 || digitsOnly.length === 9;
}

/**
 * Validates US visa number format
 * Must be exactly 8 characters: either all numeric (12345678) or 1 letter + 7 digits (A1234567)
 */
export function isValidVisaNumber(visaNum: string | undefined | null): boolean {
  if (!visaNum) return false;
  const cleaned = visaNum.trim().toUpperCase();

  return VISA_NUMBER_FORMAT.test(cleaned);
}

/**
 * Validates work permit (EAD) number format
 * Must be 13 characters: 3 letters + 10 digits (e.g., EAC1234567890)
 */
export function isValidWorkPermitNumber(workPermitNum: string | undefined | null): boolean {
  if (!workPermitNum) return false;
  const cleaned = workPermitNum.trim().toUpperCase();

  return WORK_PERMIT_NUMBER_FORMAT.test(cleaned);
}

/**
 * Validates passport number format
 * Must be 6-15 alphanumeric characters
 */
export function isValidPassportNumber(passportNum: string | undefined | null): boolean {
  if (!passportNum) return false;

  return PASSPORT_NUMBER_FORMAT.test(passportNum.trim());
}

/**
 * Validates a date string is in MM/DD/YYYY format and represents a valid date
 */
export function isValidDateString(dateString: string | undefined | null): boolean {
  if (!dateString) return false;

  if (!DATE_STRING_FORMAT.test(dateString)) return false;

  const [month, day, year] = dateString.split('/').map(Number);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * Validates that a boolean field has been set (true or false, not undefined/null)
 */
export function isBooleanSet(value: boolean | undefined | null): boolean {
  return value === true || value === false;
}

/**
 * Validates that a field has a truthy value (for required fields that aren't strings)
 */
export function isFieldSet(value: unknown): boolean {
  return !!value;
}

/**
 * Helper for validating OF-306 style yes/no questions with list entries
 * Pattern: boolean field + array of saved items + draft item
 * Returns true if:
 * - hasX is explicitly false (user answered "no")
 * - hasX is true AND draft is empty (or complete) - regardless of saved items
 * Returns false if:
 * - hasX is not set
 * - hasX is true AND draft is partially filled (not empty and not complete)
 */
export function validateYesNoWithList<TItem, TDraft>(
  hasValue: boolean | null | undefined,
  savedItems: TItem[] | undefined,
  draftItem: TDraft | undefined,
  isDraftComplete: (draft: TDraft) => boolean,
  isDraftEmpty: (draft: TDraft) => boolean,
): boolean {
  if (!isBooleanSet(hasValue)) return false;
  if (hasValue === false) return true;
  if (!draftItem) return false;

  const draftIsEmpty = isDraftEmpty(draftItem);
  const draftIsComplete = isDraftComplete(draftItem);

  if (draftIsEmpty) {
    return Boolean(savedItems && savedItems.length > 0);
  }

  if (draftIsComplete) return true;

  return false;
}
